import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import PayrollService from "../../services/payrollService";

export const fetchBatchHistory = createAsyncThunk(
  "history/fetchBatchHistory",
  async ({ params, key }, { rejectWithValue }) => {
    try {
      const res = await PayrollService.getBatchHistory(params);
      return {
        key,
        data: res.data?.data || [],
        totalRecords: res.data?.totalRecord || 0,
      };
    } catch (err) {
      return rejectWithValue({ key, error: "Payroll history fetch failed" });
    }
  }
);

export const fetchEmployeeBatchHistory = createAsyncThunk(
  "history/fetchEmployeeBatchHistory",
  async ({ params, key }, { rejectWithValue }) => {
    try {
      const res = await PayrollService.getEmployeeBatchHistory(params);
      return {
        key,
        data: res.data?.data || [],
        totalRecords: res.data?.totalRecord || 0,
      };
    } catch (err) {
      return rejectWithValue({ key, error: "Employee batch history fetch failed" });
    }
  }
);

export const exportPayrollHistoryCSV = createAsyncThunk(
  "user/exportPayrollHistoryCSV",
  async (params, thunkAPI) => {
    try {
      const res = await PayrollService.exportPayrollHistoryCSV(params);

      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = "history.csv";
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      return thunkAPI.rejectWithValue("Export Payroll History CSV Error");
    }
  }
);

const historySlice = createSlice({
  name: "history",
  initialState: {
    listDataByKey: {},
    totalByKey: {},
    listLoadingByKey: {},
    entityByKey: {},
    entityLoadingByKey: {},
    entityErrorByKey: {},
    exportCSVLoading: false,
    isSuccess: false,

    filters: {
      search: "",
      startDate: null,
      endDate: null,
      status: "",
    },
  },

  reducers: {
    setHistoryFilters(state, action) {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
    resetHistoryFilters(state) {
      state.filters = {
        search: "",
        startDate: null,
        endDate: null,
        status: "",
      };
    },
    clearHistoryError(state, action) {
      const key = action.payload;
      if (key) {
        state.entityErrorByKey[key] = null;
      }
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(exportPayrollHistoryCSV.pending, (state) => {
        state.exportCSVLoading = true;
        state.isSuccess = true;
      })
      .addCase(exportPayrollHistoryCSV.fulfilled, (state) => {
        state.exportCSVLoading = false;
        state.isSuccess = true;
      })
      .addCase(exportPayrollHistoryCSV.rejected, (state) => {
        state.exportCSVLoading = false;
        state.isSuccess = false;
      })

      .addMatcher(
        (a) => a.type.endsWith("/pending") && a.meta.arg?.key,
        (state, action) => {
          const key = action.meta.arg.key;
          state.listLoadingByKey[key] = true;
          state.entityLoadingByKey[key] = true;
          state.entityErrorByKey[key] = null;
        }
      )

      .addMatcher(
        (a) => a.type.endsWith("/fulfilled") && a.payload?.key,
        (state, action) => {
          const { key, data, totalRecords } = action.payload;

          if (Array.isArray(data)) {
            state.listDataByKey[key] = data;
            state.totalByKey[key] = totalRecords || 0;
            state.listLoadingByKey[key] = false;
          } else {
            state.entityByKey[key] = data;
            state.entityLoadingByKey[key] = false;
          }
        }
      )

      .addMatcher(
        (a) => a.type.endsWith("/rejected") && a.payload?.key,
        (state, action) => {
          const { key, error } = action.payload;
          state.listLoadingByKey[key] = false;
          state.entityLoadingByKey[key] = false;
          state.entityErrorByKey[key] = error;
        }
      );
  },
});

export const {
  setHistoryFilters,
  resetHistoryFilters,
  clearHistoryError,
} = historySlice.actions;

export default historySlice.reducer;
